import { useEffect, useRef } from 'react';
import { valueProps } from '@/mocks/homeContent';

export default function ValuePropsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll('.reveal');
            reveals.forEach((r) => r.classList.add('visible'));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="value-props" className="relative py-24 md:py-32 px-4 md:px-6 lg:px-12 bg-background-50">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="reveal text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-background-300/60 text-xs font-medium text-foreground-600 tracking-wide uppercase bg-transparent mb-4">
            Why Cybi Solutions
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 tracking-tight">
            Built differently, engineered for results
          </h2>
          <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
            We are not just an IT provider. We are an engineering partner that builds and manages the technology infrastructure modern businesses need to thrive.
          </p>
        </div>

        {/* Grid: 4 on top, 3 on bottom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {valueProps.map((prop, idx) => (
            <div
              key={prop.title}
              className={`reveal reveal-delay-${idx + 1} group relative p-6 rounded-xl bg-background-50 border border-background-200/60 hover:bg-primary-500 hover:border-primary-500 hover:scale-105 transition-all duration-300 cursor-default`}
            >
              <div className="w-11 h-11 rounded-lg bg-background-100 group-hover:bg-white/20 flex items-center justify-center mb-4 transition-colors duration-300">
                <i className={`${prop.icon} text-lg transition-colors duration-300 text-primary-500 group-hover:text-white w-5 h-5 flex items-center justify-center`} />
              </div>
              <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-white transition-colors duration-300 mb-2">
                {prop.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 group-hover:text-white/80 transition-colors duration-300 leading-relaxed">
                {prop.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}